import React from "react";
import { Link as pageLink } from "gatsby";
import { IGatsbyImageData } from "gatsby-plugin-image";
import type { SystemStyleObject } from "@indeed/ifl-css";
import { Box, Flex, Heading, Text, TextLink } from "@indeed/ifl-components";
import { Grid, GridItem } from "components/grid";
import { ArticleCardImg } from "components/cards/articleCardImg";
import { formatDate } from "helpers/utils";
import { useAccessibleCard } from "hooks/useAccessibleCard";
import { focusVisibleStyles } from "styles/common";

export interface ArticlesSecondaryCardProps {
  altText: string;
  author: string;
  excerpt: string;
  gatsbyImageData: IGatsbyImageData;
  image: string;
  publishDate?: string;
  slug: string;
  title: string;
  sx?: SystemStyleObject;
}

export const ArticleCardMobile: React.FC<ArticlesSecondaryCardProps> = ({
  altText,
  author,
  gatsbyImageData,
  image,
  publishDate,
  slug,
  sx,
  title,
}) => {
  const { cardRef, handleClick } = useAccessibleCard();

  return (
    <Box
      as="article"
      ref={cardRef}
      onClick={handleClick}
      sx={[
        {
          ":hover": {
            cursor: "pointer",
            "a h3": {
              color: "primary.800",
              textDecoration: "underline",
            },
          },
        },
        sx,
      ]}
    >
      <Grid templateColumns="7fr 15fr" columnGap={3}>
        <GridItem>
          <ArticleCardImg
            image={image}
            gatsbyImageData={gatsbyImageData}
            alt={altText}
            sx={{ objectFit: "cover" }}
          />
        </GridItem>
        <GridItem>
          <Flex as="figcaption" sx={{ flexDirection: "column" }}>
            <TextLink
              as={pageLink}
              to={`/article/${slug}`}
              sx={{ borderBottom: "none", ...focusVisibleStyles }}
            >
              <Heading
                as="h3"
                level={4}
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.25,
                  mb: "8px",
                  mt: [null, null, 6],
                }}
              >
                {title}
              </Heading>
            </TextLink>
            <Flex sx={{ flexDirection: ["row", null, null, "column"] }}>
              <Text
                sx={{
                  fontSize: 2,
                  fontWeight: 700,
                  lineHeight: 1.5,
                }}
              >
                {author}
              </Text>
              {publishDate && (
                <>
                  <Box
                    sx={{
                      display: ["block", null, null, "none"],
                      width: "1px",
                      backgroundColor: "neutral.500",
                      ml: "8px",
                      mr: "8px",
                    }}
                  />
                  <Text sx={{ fontSize: 2, fontWeight: 500, lineHeight: 1.5 }}>
                    {formatDate(publishDate)}
                  </Text>
                </>
              )}
            </Flex>
          </Flex>
        </GridItem>
      </Grid>
    </Box>
  );
};
